// 時間前入札の「入札制限」(= settings「入札制限」の 時間前 matrix) 照合 (= 純関数)。
//
// 設定値は入札制限画面 (`docs/specs/bidding/bid-limits.md` §2-2) がスポットとは独立に
// 保持し、AC-BDL-014 が「保存した draft を 時間前入札画面のチェックに反映する」ことを
// 求める。画面側の AC は AC-BIN-015 (= 違反した札だけを提出の対象から外し、 違反して
// いない札は提出する。 要承認の承認申請は違反を解消するまで申請自体を止める)。
// 本モジュールの事前チェックを使うのは、 承認申請になる札だけ。 1 札の軸は申請する札を、
// コマ計の軸は申請する札と受渡日の既存の札 (母集合) を `IntradayPreCheckInput` で受け取る。
//
// 判定そのもの (= セル群 → (エリア × 売買) 別制限 → 札 1 件の突合) はスポットと共通の
// `~/models/bidLimits` に置く。本モジュールは時間前の表示行 (IntradayBidRow) を
// その判定に流し込み、提出確認モーダルの違反明細 (ViolationSection) に整形する。
//
// backend も同じ設定を読んで DRAFT 離脱時 / 承認時に再検証する
// (`IntradayOrderUsecase.ensureWithinIntradayBidLimits`)。ここでの判定は UX 事前
// チェックで、業務制約の SoT は backend 側。
//
// 上限数量(コマ計) は、 同一 (エリア × コマ × 売買) の有効な札の合計で判定する
// (bid-limits.md §2-2、 時間前はスポットと独立のセル)。 backend が申請・提出・承認と
// CSV 確定で判定して拒否し (`IntradayOrderUsecase`)、 画面は要承認の承認申請を申請ごと
// 止めるために同じ合算を事前に行う (AC-BIN-015 の ※、 `detectIntradayVolumeTotalViolations`)。
// 直接提出の札の コマ計 は画面で判定せず、 拒否の明細 (`intradaySubmitViolation.ts`、
// AC-BIN-052) を表示するだけ。
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import {
  BiddingApprovalState,
  BiddingIntradayOrderStatus,
} from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import i18next from 'i18next'
import type { TFunction } from 'i18next'

import type { ViolationSection } from '~/components/bidding/common/SubmitConfirmModal'
import { convertAreaLabel } from '~/models/area'
import type {
  AreaSideLimit,
  BidLimitViolationKind,
  BidLimits,
  VolumeCountableBid,
} from '~/models/bidLimits'
import {
  checkBidLimit,
  detectVolumeTotalViolations,
  resolveAreaSideLimit,
} from '~/models/bidLimits'
import { violatesIntradayJepxRangeForRow } from '~/pages/IntradayBidding/models/intradayBidAggregation'
import {
  type IntradayBidRow,
  isMarketOrderRow,
  isWithdrawnBidRow,
} from '~/pages/IntradayBidding/models/intradayBidRow'
import {
  type IntradayBidLimitViolationDetail,
  formatIntradayBidLimitViolationDetail,
} from '~/pages/IntradayBidding/models/intradaySubmitViolation'

/** 違反した軸の設定値 (checkBidLimit が違反を返した軸は必ず設定されている)。 */
const limitValueOf = (
  kind: BidLimitViolationKind,
  limit: AreaSideLimit,
): number | undefined => {
  switch (kind) {
    case 'price-min':
      return limit.priceMin
    case 'price-max':
      return limit.priceMax
    case 'volume-max':
      return limit.qtyMax
  }
}

/**
 * 札行ごとに 時間前 の入札制限 (エリア × 売買の価格上下限・上限数量) との違反を検出する。
 * 制限が未設定の (エリア × 売買) は「制限未設定 = 違反なし」とする (= mock フォール
 * バックは持たない)。
 *
 * 時間前入札は指値のみ (= 成行は入札制限画面の許可が前提で backend 未対応) なので、
 * スポットの `detectLimitViolations` と違い成行の価格軸除外は持たない。backend の
 * `ensureWithinIntradayBidLimits` も価格を常に `option.New(...)` で渡しており、
 * 同じ規則で揃えている (= 片側だけが許可する状態を作らない)。
 *
 * 返す明細は SUBMIT RPC 失敗から組む明細 (`intradaySubmitViolation.ts`) と同じ型で、
 * 同じ整形関数を通す (= 同じ違反がどちらの経路で見つかっても同じ文言になる)。
 *
 * @param bids - 検証対象の札一覧 (= 呼び出し元が提出対象等に絞ってから渡す)
 * @param limits - BFF GetBidLimitMatrix の INTRADAY セルから組んだ (エリア × 売買) 別制限
 */
export const detectIntradayLimitViolations = (
  bids: readonly IntradayBidRow[],
  limits: BidLimits,
): IntradayBidLimitViolationDetail[] => {
  const out: IntradayBidLimitViolationDetail[] = []
  for (const b of bids) {
    // 取下済の札は JEPX へ到達しないので枠を解放する (= 提出対象の母集合と同じ規則)。
    if (isWithdrawnBidRow(b)) continue
    const limit = resolveAreaSideLimit(limits, b.areaCode, b.direction)
    if (limit === undefined) continue
    const price = Number(b.priceYenPerKwh)
    // 制限 QTY も札の数量も MW (issue #4285)。 単位換算は挟まない。
    const volumeMw = Number(b.volumeMw)
    for (const kind of checkBidLimit(price, volumeMw, limit)) {
      const limitValue = limitValueOf(kind, limit)
      if (limitValue === undefined) continue
      out.push({
        bidId: b.id,
        kind: kind === 'volume-max' ? 'qty-max' : kind,
        areaCode: b.areaCode,
        side: b.direction,
        timeCode: b.timeCode,
        actual: kind === 'volume-max' ? volumeMw : price,
        limit: limitValue,
      })
    }
  }
  return out
}

/**
 * 上限数量(コマ計) の合計に、 既に出ている札 1 件が数える数量 MW。
 *
 * backend `SumActiveIntradayOrderVolume` (`bidding/intraday_order_repository.go`) の札ごとの
 * 規則と同じ。 時間前はザラバで即約定するので、 約定済みの札も外さない (外すと約定のたびに
 * 枠が空き、 上限を何度でもくぐれる)。 規則を変える時は backend と一緒に変える。
 *   - 承認申請中 (PENDING)・承認済 (APPROVED): 入札量
 *   - 承認軸 SUBMITTED (板に出た札): 入札中・送信失敗・全約定は入札量。 一部約定は指値なら
 *     入札量 (板に残りがある)、 成行なら約定量 (残りは JEPX が削除済み)。 未約定・失効・取消は 0
 *   - 取下げ済 (CANCELLED): 約定量 (約定が無ければ 0)
 *   - 下書き・差戻し・提出失敗: 0
 */
export const volumeCountedForQtyTotalMw = (b: IntradayBidRow): number => {
  const volume = Number(b.volumeMw)
  const matched = b.execVolumeMw === undefined ? 0 : Number(b.execVolumeMw)
  switch (b.approvalState) {
    case BiddingApprovalState.PENDING:
    case BiddingApprovalState.APPROVED:
      return volume
    case BiddingApprovalState.SUBMITTED:
      switch (b.bidStatus) {
        case BiddingIntradayOrderStatus.PLACED:
        case BiddingIntradayOrderStatus.FAILED:
        case BiddingIntradayOrderStatus.ALL_MATCHED:
          return volume
        case BiddingIntradayOrderStatus.PARTIAL_MATCHED:
          return isMarketOrderRow(b) ? matched : volume
        default:
          return 0
      }
    case BiddingApprovalState.CANCELLED:
      return matched
    default:
      return 0
  }
}

/** 上限数量(コマ計) を合算する単位 (エリア × コマ × 売買) の key。 */
const bucketKeyOf = (b: {
  timeCode: number
  areaCode: AreaCode
  direction: Side
}): string => `${b.timeCode}|${b.areaCode}|${b.direction}`

/** 承認申請を止める事前チェックの入力 (全て同じ受渡日の札)。 */
export type IntradayPreCheckInput = {
  /**
   * 今回の提出で承認申請 (REQUEST) になる札 (下書き)。 1 札の軸はこの札を判定し、
   * 上限数量(コマ計) はこの札を含む (エリア × コマ × 売買) だけを判定する。
   * 承認モードはエリア単位なので、 同じバケットの下書きは全て承認申請になる。
   */
  requestBids: readonly IntradayBidRow[]
  /**
   * その受渡日の全ての札 (上限数量(コマ計) の母集合)。 `requestBids` と同じ札が入っていても
   * 二重に数えない (id で除く。 backend の `excludeOrderID` と同じ)。
   */
  existingBids: readonly IntradayBidRow[]
}

/**
 * 要承認の承認申請を止める 上限数量(コマ計) の事前チェック (AC-BIN-015 の ※)。
 *
 * 承認申請になる札を含む (エリア × コマ × 売買) ごとに、 既に出ている札の合計
 * (`volumeCountedForQtyTotalMw`) と今回申請する札の合計を足し、 時間前のセルの
 * 上限数量(コマ計) を超えたら 1 件の `qty-total` 明細を返す (ちょうど上限は許容、 未設定は
 * 制限なし。 合算・丸め・境界はスポットと共通の `detectVolumeTotalViolations`)。
 * `bidId` はそのバケットで最初の承認申請になる札 (見出しの件数はこの id でなく、 バケットの
 * 全ての承認申請になる札で数える。 `buildIntradayLimitViolationSection`)。
 *
 * 判定の正は backend (`ensureWithinIntradayVolumeTotalLimit`) で、 ここは UX の事前チェック。
 * 画面の一覧は定期的に取り直すだけなので、 画面の後に他の操作で積まれた分は backend が拒否する。
 */
export const detectIntradayVolumeTotalViolations = (
  input: IntradayPreCheckInput,
  limits: BidLimits,
): IntradayBidLimitViolationDetail[] => {
  const firstRequestOf = new Map<string, IntradayBidRow>()
  for (const b of input.requestBids) {
    const key = bucketKeyOf(b)
    if (!firstRequestOf.has(key)) firstRequestOf.set(key, b)
  }
  if (firstRequestOf.size === 0) return []
  const requestIds = new Set(input.requestBids.map((b) => b.id))
  const population: VolumeCountableBid[] = [
    ...input.existingBids
      .filter(
        (b) => !requestIds.has(b.id) && firstRequestOf.has(bucketKeyOf(b)),
      )
      .map((b) => ({
        areaCode: b.areaCode,
        timeCode: b.timeCode,
        direction: b.direction,
        volumeMwh: String(volumeCountedForQtyTotalMw(b)),
      })),
    ...input.requestBids.map((b) => ({
      areaCode: b.areaCode,
      timeCode: b.timeCode,
      direction: b.direction,
      volumeMwh: b.volumeMw,
    })),
  ]
  const out: IntradayBidLimitViolationDetail[] = []
  for (const v of detectVolumeTotalViolations(population, limits)) {
    const first = firstRequestOf.get(
      bucketKeyOf({
        timeCode: v.timeCode,
        areaCode: v.areaCode,
        direction: v.side,
      }),
    )
    if (first === undefined) continue
    out.push({
      bidId: first.id,
      kind: 'qty-total',
      areaCode: v.areaCode,
      side: v.side,
      timeCode: v.timeCode,
      actual: v.actual,
      limit: v.limit,
    })
  }
  return out
}

const sideLabelOf = (side: Side): string => {
  const t = i18next.t.bind(i18next)
  return side === Side.BUY
    ? t('common:tradeSide.buy')
    : t('common:tradeSide.sell')
}

/** 明細行の共通見出し (design intraday.html の違反リスト行頭と同じ粒度)。 */
const detailHead = (timeCode: number, areaCode: AreaCode, side: Side): string =>
  i18next.t('page:biddingIntraday.violation.detailHead', {
    timeCode,
    area: convertAreaLabel(areaCode),
    side: sideLabelOf(side),
  })

/**
 * 提出確認モーダルに積むテナント設定上限違反のセクション (AC-BIN-015)。
 *
 * JEPX 制度上限違反 (AC-BIN-038) とは独立した違反種別として別枠に出す
 * (AC-BIN-039 / design intraday.html `intraday-submit-viol-tenant`)。 制度上限は
 * 自社では変えられず、テナント設定上限は入札制限画面で変更できるため、混ぜると
 * 「どちらを直せば送れるのか」が判断できない。
 *
 * `details` が空のセクションは `SubmitConfirmModal` 側で描画されない
 * (= 違反ゼロのとき見出しだけが出ることはない)。
 *
 * 見出しの件数は違反した札の数 (確定後に出す RPC 由来の節と同じ単位、 AC-BIN-052)。
 * 1 つの札が複数の軸で違反しても 1 件。 上限数量(コマ計) の違反は、 そのバケットで承認申請に
 * なる札を全て違反した札に数える (申請ごと止めるのでどの札も申請されず、 札の並び順で件数が
 * 変わらない)。 明細行は軸ごとに出し、 コマ計はバケットに 1 行。
 */
export const buildIntradayLimitViolationSection = (
  input: IntradayPreCheckInput,
  limits: BidLimits,
  t: TFunction,
): ViolationSection => {
  const perBid = detectIntradayLimitViolations(input.requestBids, limits)
  const volumeTotal = detectIntradayVolumeTotalViolations(input, limits)
  const overBuckets = new Set(
    volumeTotal.map((v) =>
      bucketKeyOf({
        timeCode: v.timeCode,
        areaCode: v.areaCode,
        direction: v.side,
      }),
    ),
  )
  const violatedBidIds = new Set([
    ...perBid.map((v) => v.bidId),
    ...input.requestBids
      .filter((b) => overBuckets.has(bucketKeyOf(b)))
      .map((b) => b.id),
  ])
  return {
    title: t('page:biddingIntraday.violation.tenantLimitTitle', {
      count: violatedBidIds.size,
    }),
    body: t('page:biddingIntraday.violation.tenantLimitBody'),
    details: [...perBid, ...volumeTotal].map((v) =>
      formatIntradayBidLimitViolationDetail(v, t),
    ),
  }
}

/**
 * 入札制限 (settings「入札制限」) を取得できていない時に積む fail-closed セクション。
 *
 * 取得失敗のまま上の判定を通すと「制限未設定 = 違反なし」に見えるだけで検証が成立して
 * いない (= 空 lookup)。「違反なし」と「検証できていない」を確認モーダル上で区別し、
 * 確定ボタンを非活性にするためにセクションを 1 つ立てる (スポットの
 * `buildBidLimitsUnavailableSection` と同じ扱い)。 積むのは事前チェックの対象
 * (承認申請になる札) がある時だけ。 直接提出だけの提出は画面が入札制限を使わず、
 * backend が検証する。
 */
export const buildIntradayBidLimitsUnavailableSection = (
  t: TFunction,
): ViolationSection => ({
  title: t('page:biddingIntraday.violation.limitUnavailableTitle'),
  body: t('page:biddingIntraday.violation.limitUnavailableBody'),
  details: [t('page:biddingIntraday.violation.limitUnavailableDetail')],
})

/**
 * 提出確認モーダルに積む JEPX 制度上限違反 (AC-BIN-038) のセクション。
 *
 * `SubmitConfirmModal` は `details` が空のセクションを描画しない
 * (`violationSections.filter((s) => s.details.length > 0)`) ため、 テナント設定
 * 上限違反 (`buildIntradayLimitViolationSection`) が 1 件でもあると、 JEPX 制度上限
 * 違反だけが検出されたケース用の汎用 Alert フォールバック (`hasViolation &&
 * sections.length === 0`) が効かなくなり、 制度上限違反が確認モーダルから一切
 * 表示されなくなっていた (codex 系列レビューで検出。AC-BIN-039「区別して表示する」未達)。
 * 本関数は違反行を「どの札か (コマ・エリア・売買)」だけ明細に積み、 制度上限違反の
 * 存在をテナント設定違反と独立した節として必ず出す。 価格・数量の許容レンジまで含めた
 * 明細化 (AC-BIN-039 の残作業。 spot `detectJepxLimitViolations` 相当) は本 PR の
 * scope 外のまま据え置く。
 */
export const buildIntradayJepxViolationSection = (
  bids: readonly IntradayBidRow[],
  t: TFunction,
): ViolationSection => {
  const details = bids
    .filter((b) => !isWithdrawnBidRow(b))
    .filter(violatesIntradayJepxRangeForRow)
    .map((b) => detailHead(b.timeCode, b.areaCode, b.direction))
  return {
    title: t('page:biddingIntraday.violation.jepxTitle', {
      count: details.length,
    }),
    body: t('page:biddingIntraday.violation.jepxBody'),
    details,
  }
}
